import { memo } from 'react';
import { APIProvider, Map, RenderingType } from '@vis.gl/react-google-maps';
import { ServiceAreaType } from '../constants';
import DrawingWrapper from './DrawingWrapper';
import SearchBox from './SearchBox';

interface FormErrors {
  name?: string;
  shape?: string;
}

interface ExistingShape {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
  color: string;
}

interface OptimizedMapProps {
  mapCenter: { lat: number; lng: number };
  zoom: number;
  color: string;
  onOverlayComplete: (overlay: google.maps.Polygon | google.maps.Circle) => void;
  initialShapeData?: {
    type: ServiceAreaType;
    vertices?: [number, number][];
    centroid?: [number, number];
    range?: number;
  };
  onOverlayClear: () => void;
  errors: FormErrors;
  existingShapes?: ExistingShape[];
  showExistingShapes?: boolean;
  onToggleExistingShapes?: (visible: boolean) => void;
}

// Memoized map component to prevent unnecessary re-renders
const OptimizedMap = memo<OptimizedMapProps>(
  ({
    mapCenter,
    zoom,
    color,
    onOverlayComplete,
    initialShapeData,
    onOverlayClear,
    errors,
    existingShapes,
    showExistingShapes = true,
    onToggleExistingShapes
  }) => {
    return (
      <div className="space-y-1">
        <div className="h-[550px] w-full overflow-hidden rounded-lg">
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''} libraries={[]}>
            <Map
              renderingType={RenderingType.RASTER} // Changed from VECTOR to RASTER for lower cost
              gestureHandling="cooperative" // Changed from "greedy" to reduce accidental interactions
              disableDefaultUI={true} // Disable all UI to reduce API calls
              mapId="SERVICE_AREA_MAP"
              defaultCenter={mapCenter}
              mapTypeControl={false}
              defaultZoom={Math.min(zoom, 18)} // Limit max zoom to reduce tile loading costs
              zoomControl={true}
              fullscreenControl={false}
              streetViewControl={false}
              keyboardShortcuts={false} // Disable to reduce interactions
              clickableIcons={false} // Disable POI clicks to reduce API calls
              restriction={{
                latLngBounds: {
                  north: 80,
                  south: -80,
                  west: -180,
                  east: 180
                },
                strictBounds: true
              }}
              style={{ width: '100%', height: '100%' }}>
              <SearchBox />
              <DrawingWrapper
                color={color}
                onOverlayComplete={onOverlayComplete}
                initialData={initialShapeData}
                onOverlayClear={onOverlayClear}
                existingShapes={existingShapes}
                showExistingShapes={showExistingShapes}
                onToggleExistingShapes={onToggleExistingShapes}
              />
            </Map>
          </APIProvider>
        </div>
        {errors.shape && <div className="text-sbase font-semibold text-error">{errors.shape}</div>}
      </div>
    );
  }
);

OptimizedMap.displayName = 'OptimizedMap';

export default OptimizedMap;
